// lib
import { useReducer, useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import { dataReducer } from '@aytm/app/stats/page/question-views/components/questions-mapping/reducers'

// components
import { default as ComponentContainer } from '@aytm/app/stats/page/question-views/components/questions-mapping/Component'

import Entities from '@aytm/app/stats/page/question-views/components/questions-mapping/Entities'
import Attributes from '@aytm/app/stats/page/question-views/components/questions-mapping/Attributes'

import Controls from '@aytm/app/stats/page/question-views/components/questions-mapping/Controls'
import Sources from '@aytm/app/stats/page/question-views/components/questions-mapping/Sources'
import Footer from '@aytm/app/stats/page/question-views/components/questions-custom/Footer'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

const Component = ({
  model,
  data,
  states,
  disabled,
  onChange,
  onReset,
  onFeaturesChange,
  onControlsChange,
  onModeChange,
  onUpdateData
}) => {
  const [state, dispatch] = useReducer(dataReducer, {
    entities: JSON.parse(JSON.stringify(model.entities)),
    attributes: JSON.parse(JSON.stringify(model.attributes)),
    errors: []
  })

  const [changed, setChanged] = useState(false)

  const timer = useRef(null)

  const attributesOptions = useMemo(
    () =>
      data.options.attributes.map(attribute => {
        const option = {
          icon: attribute.icon,
          label: attribute.label,
          search: attribute.search
        }

        const refs = state.entities.map(entity => {
          const i = data.options.entities.findIndex(v => v.pattern === entity.pattern)
          return attribute.refs[i]
        })

        option.key = refs.join(', ')
        option.text = `<b>${option.label}</b><br/>${option.key}`

        return option
      }),
    [data.options.entities, data.options.attributes, state.entities]
  )

  const checkModel = () => {
    const newData = {}
    if (!isEqual(state.entities, model.entities)) {
      newData.errors ||= JSON.parse(JSON.stringify(state.errors))
      for (const i in state.entities) {
        const index = newData.errors.indexOf(`e${Number(i) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.entities = JSON.parse(JSON.stringify(model.entities))
    }
    if (!isEqual(state.attributes, model.attributes)) {
      newData.errors ||= JSON.parse(JSON.stringify(state.errors))
      for (const i in state.attributes) {
        const index = newData.errors.indexOf(`a${Number(i) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.attributes = JSON.parse(JSON.stringify(model.attributes))
    }
    return newData
  }
  const checkState = () => {
    const newData = {}
    if (!isEqual(state.entities, model.entities)) newData.entities = JSON.parse(JSON.stringify(state.entities))
    if (!isEqual(state.attributes, model.attributes)) newData.attributes = JSON.parse(JSON.stringify(state.attributes))
    return newData
  }

  const checkError = () => {
    if (0 < state.errors.length || !state.entities.length || !state.attributes.length) {
      return { error: true }
    }
    if (state.attributes.some(attr => attr.refs.split(',').length < state.entities.length)) {
      return {
        error:
          'For each attribute, please make sure there is a question number or subquestion number that corresponds to each entity.'
      }
    }
    return { error: false }
  }

  const pulled = useRef(false)

  useEffect(() => {
    if (changed) return

    pulled.current = true
    dispatch({
      type: 'RESET',
      data: { ...checkModel() }
    })

    // The pull leaves the state equal to the model, so nothing local is pending.
    setChanged(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.entities, model.attributes])

  useEffect(() => {
    // The state a pull wrote is the model's own, so it is not a local edit to protect; arming the
    // flag for it held off the next pull, and a reset landing then left the typed rows on screen.
    if (!pulled.current) setChanged(true)
    pulled.current = false

    const newData = {
      ...checkError(),
      ...checkState()
    }

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      onChange(newData)

      setChanged(false)
    }, 300)

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const dataContextValue = useMemo(() => {
    return {
      id: model.id,
      mode: model.mode,
      group: model.group,
      original: model.original,
      data,
      disabled,
      attributesOptions,
      dispatch
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, model.mode, model.group, model.original, data, disabled, attributesOptions, dispatch])

  return (
    <BlockWrapper>
      <ComponentContainer
        Controls={Controls}
        Footer={Footer}
        model={model}
        data={data}
        states={states}
        prefix="Concept"
        changed={changed}
        disabled={disabled}
        dataContextValue={dataContextValue}
        onFeaturesChange={onFeaturesChange}
        onControlsChange={onControlsChange}
        onUpdateData={onUpdateData}
      >
        <Sources source={model.source} data={data} entities={state.entities} disabled={disabled} dispatch={dispatch} />
        <Entities items={state.entities} onReset={onReset} />
        <Attributes items={state.attributes} dimension={state.entities.length} onModeChange={onModeChange} />
      </ComponentContainer>
    </BlockWrapper>
  )
}

Component.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  onFeaturesChange: PropTypes.func,
  onControlsChange: PropTypes.func,
  onModeChange: PropTypes.func,
  onUpdateData: PropTypes.func
}

Component.displayName = 'Component'

export default Component
